import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, PackageCheck, RefreshCw, Star } from 'lucide-react';

const useCases = [
  {
    icon: ShoppingCart,
    tag: 'Most popular',
    title: 'Cart Abandonment Recovery',
    description:
      'A customer adds items but never checks out. Our agent calls within minutes, in their language, reminds them of exactly what they left, answers questions, and brings them back to checkout.',
    metric: 'Avg. recovery window: under 10 minutes after abandonment',
  },
  {
    icon: PackageCheck,
    tag: 'High ROI',
    title: 'COD Order Confirmation',
    description:
      'Cash-on-delivery orders are your biggest RTO risk. Our agent calls every new COD customer to confirm intent before you dispatch — dramatically reducing return-to-origin losses.',
    metric: 'One confirmed call can save Rs. 150+ in reverse logistics',
  },
  {
    icon: Star,
    tag: 'Retention',
    title: 'Post-Purchase Follow-up',
    description:
      'Automated voice calls after delivery to check satisfaction, collect reviews, and flag any issues early. Keeps customers engaged and reduces support escalations.',
    metric: 'Higher NPS and repeat purchase rate from day-1 follow-ups',
  },
  {
    icon: RefreshCw,
    tag: 'Support',
    title: 'Return and Exchange Handling',
    description:
      'Let customers initiate a return or exchange through a natural voice conversation — no forms, no hold music, no waiting. The agent logs the request and routes it to your ops team instantly.',
    metric: 'Faster resolution, lower support ticket volume',
  },
];

const Industries = () => {
  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="py-24 md:py-32 bg-[#FAFAF9]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#2563EB] font-semibold text-sm uppercase tracking-wider">
            Use cases
          </span>
          <h2
            data-testid="use-cases-headline"
            className="mt-4 font-['DM_Sans'] text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1C1917] tracking-tight"
          >
            Built for every D2C moment that matters.
          </h2>
          <p className="mt-4 text-[#57534E] text-base md:text-lg max-w-2xl mx-auto">
            Cart abandonment is just the start. Neurobloom handles the full post-purchase
            lifecycle for Indian D2C brands — in any supported language.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {useCases.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`use-case-${index}`}
              className="bg-white rounded-3xl p-8 border border-[#E7E5E4] shadow-sm card-hover flex flex-col"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-14 h-14 bg-[#EFF6FF] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-7 h-7 text-[#2563EB]" />
                </div>
                <span className="text-xs font-semibold bg-[#F0FDF4] text-[#16A34A] px-3 py-1 rounded-full border border-[#BBF7D0]">
                  {item.tag}
                </span>
              </div>
              <h3 className="font-['DM_Sans'] text-xl font-semibold text-[#1C1917] mb-3">
                {item.title}
              </h3>
              <p className="text-[#57534E] leading-relaxed flex-1">{item.description}</p>
              <div className="mt-5 pt-5 border-t border-[#E7E5E4]">
                <p className="text-xs text-[#78716C] font-medium">{item.metric}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Industries;
